/**
 * A TÁR ÁLLAPOTA (#74) — a keresés, a szűrők, a lap és a kijelölés.
 *
 * A szerver szűr (`GET /archive`), nem a felület: a Tár egy cég ÖSSZES
 * bizonylata, évekre visszamenőleg — azt nem töltjük le, hogy a böngésző
 * szűrjön benne. A felület annyit tart, amennyit lát, és lapoz.
 *
 * A billentyűk a többi felület szójegyzékét követik: `/` keres, `↑↓`/`j`/`k`
 * mozog, `Enter`/`→` megnyit, `Esc` vissza a listához. Az `i` az időszak
 * „Ettől" mezőjére visz (Viktor, 10-23: billentyűvel is könnyű legyen).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Shortcut } from '@/hooks/useShortcuts';
import { ApiError, api } from '@/lib/api-client';
import { type ParsedPeriod, parsePeriodInput } from './period';

export type ArchiveDocType =
  | 'INBOUND_INVOICE'
  | 'OUTBOUND_INVOICE'
  | 'RECEIPT'
  | 'BANK_STATEMENT'
  | 'OTHER';

export interface ArchiveItem {
  id: string;
  docType: ArchiveDocType;
  direction: 'INBOUND' | 'OUTBOUND';
  status: 'ARRIVED' | 'FILED' | 'NOT_DEDUCTIBLE';
  processingState: 'RECEIVED' | 'AWAITING_DATA' | 'DATA_READY';
  partnerName: string | null;
  partnerTaxNumber: string | null;
  invoiceNumber: string | null;
  issueDate: string | null;
  grossAmount: string | null;
  currency: string | null;
  period: string | null;
}

export interface ArchiveFacets {
  docTypes: { docType: ArchiveDocType; count: number }[];
  periods: { period: string | null; count: number }[];
  partners: { partnerTaxNumber: string; partnerName: string | null; count: number }[];
}

interface ArchivePage {
  items: ArchiveItem[];
  hasMore: boolean;
  total: number;
  /** A többi szűrőnek megfelelő, még le nem fűzött bizonylatok (#95). */
  unfiled: number;
  facets: ArchiveFacets;
}

export type ArchiveStatus = 'loading' | 'ready' | 'failed' | 'needCompany' | 'invalid';

/** A szerver kódja, ha a kontextusban nincs EGY cég (`archive.controller.ts`). */
const COMPANY_SELECTION_REQUIRED = 'COMPANY_SELECTION_REQUIRED';

/** A gépelés után ennyit várunk a kéréssel — nem minden betű egy kérés. */
const SEARCH_DELAY_MS = 250;

export interface ArchiveState {
  status: ArchiveStatus;
  items: ArchiveItem[];
  total: number;
  hasMore: boolean;
  facets: ArchiveFacets | null;
  query: string;
  setQuery: (value: string) => void;
  docTypes: ReadonlySet<ArchiveDocType>;
  toggleDocType: (docType: ArchiveDocType) => void;
  fromText: string;
  toText: string;
  setFromText: (value: string) => void;
  setToText: (value: string) => void;
  from: ParsedPeriod;
  to: ParsedPeriod;
  includeUndated: boolean;
  setIncludeUndated: (value: boolean) => void;
  /**
   * A még le nem fűzöttek is látszanak-e — alapból NEM (#95): a Tár az
   * eldöntött papíroké, a többi a munkalistáé.
   */
  includeUnfiled: boolean;
  setIncludeUnfiled: (value: boolean) => void;
  /** Ennyi, a többi szűrőnek megfelelő papír vár még feldolgozásra. */
  unfiled: number;
  partner: string | null;
  setPartner: (taxNumber: string | null) => void;
  clearFilters: () => void;
  loadMore: () => void;
  focusedId: string | null;
  focus: (id: string) => void;
  /** A megnyitott bizonylat — `null`, ha a lista látszik. */
  openId: string | null;
  open: (id: string) => void;
  close: () => void;
  /** A `/` és az `i` kérése — a mezők a saját effektjükben fókuszálnak. */
  searchRequest: object | null;
  periodRequest: object | null;
  /** A sor eleme — a kijelölés VALÓDI fókusz, nem csak szín (roving tabindex). */
  registerRow: (id: string, element: HTMLElement | null) => void;
  /** A mezőkből (`↓`, `Enter`) a találatokra — a kijelölt sorra. */
  focusResults: () => void;
  /** A kereső `Esc`-e: a keresés törlése, vissza a találatokra. */
  leaveSearch: () => void;
  /** A keresőből `←`-re a szűrők első elemére (Viktor, 10-26). */
  filterRequest: object | null;
  focusFilters: () => void;
  shortcuts: Shortcut[];
}

export function useArchive(active: boolean, today: Date = new Date()): ArchiveState {
  const [status, setStatus] = useState<ArchiveStatus>('loading');
  const [page, setPage] = useState<ArchivePage | null>(null);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [docTypes, setDocTypes] = useState<ReadonlySet<ArchiveDocType>>(new Set());
  const [fromText, setFromText] = useState('');
  const [toText, setToText] = useState('');
  const [includeUndated, setIncludeUndated] = useState(false);
  const [includeUnfiled, setIncludeUnfiled] = useState(false);
  const [partner, setPartner] = useState<string | null>(null);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [searchRequest, setSearchRequest] = useState<object | null>(null);
  const [periodRequest, setPeriodRequest] = useState<object | null>(null);
  // A KÉSVE ÉRKEZŐ VÁLASZ NEM ÍRHATJA FELÜL A FRISSEBBET — a gépelés közben
  // indult kérések sorrendje nem garantált.
  const generation = useRef(0);
  const rows = useRef(new Map<string, HTMLElement>());
  // A FÓKUSZ KÉRÉSRE MEGY A SORRA — nem minden újrarajzoláskor: gépelés közben
  // a frissülő lista nem vehetné el a mezőtől.
  const [focusRequest, setFocusRequest] = useState<object | null>(null);

  const day = today.toISOString().slice(0, 10);
  const from = useMemo(() => parsePeriodInput(fromText, new Date(day)), [fromText, day]);
  const to = useMemo(() => parsePeriodInput(toText, new Date(day)), [toText, day]);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const params = useMemo(() => {
    if (from === 'invalid' || to === 'invalid') {
      return null;
    }
    // A ki nem töltött szűrő nem megy el — a `null` kimarad.
    const entries: [string, string | null][] = [
      ['q', debounced === '' ? null : debounced],
      ['docType', docTypes.size > 0 ? [...docTypes].join(',') : null],
      ['from', from],
      ['to', to],
      ['undated', includeUndated ? '1' : null],
      ['partner', partner],
      ['unfiled', includeUnfiled ? '1' : null],
    ];
    return new URLSearchParams(
      entries.filter((entry): entry is [string, string] => entry[1] !== null)
    );
  }, [debounced, docTypes, from, to, includeUndated, partner, includeUnfiled]);

  const fetchPage = useCallback((search: URLSearchParams, after: string | null) => {
    const ticket = ++generation.current;
    const withCursor = new URLSearchParams(search);
    if (after !== null) withCursor.set('after', after);
    api<ArchivePage>(`/archive?${withCursor.toString()}`)
      .then((next) => {
        if (ticket !== generation.current) return;
        setPage((prev) =>
          after !== null && prev !== null
            ? { ...next, items: [...prev.items, ...next.items] }
            : next
        );
        setStatus('ready');
      })
      .catch((error: unknown) => {
        if (ticket !== generation.current) return;
        const code = error instanceof ApiError ? error.code : undefined;
        setStatus(
          code === COMPANY_SELECTION_REQUIRED
            ? 'needCompany'
            : error instanceof ApiError && error.status === 400
              ? 'invalid'
              : 'failed'
        );
      });
  }, []);

  useEffect(() => {
    if (!active) return;
    if (params === null) {
      setStatus('invalid');
      return;
    }
    setStatus('loading');
    fetchPage(params, null);
  }, [active, params, fetchPage]);

  const items = page?.items ?? [];

  // A KIJELÖLÉS A LISTÁN MARAD: ha a szűrés kivette, az elsőre lép.
  const focused = items.some((item) => item.id === focusedId) ? focusedId : (items[0]?.id ?? null);

  const move = useCallback(
    (delta: number) => {
      if (items.length === 0) return;
      const at = Math.max(
        0,
        items.findIndex((item) => item.id === focused)
      );
      const next = items[Math.min(items.length - 1, Math.max(0, at + delta))];
      setFocusedId(next.id);
      setOpenId((current) => (current === null ? null : next.id));
      setFocusRequest({});
    },
    [items, focused]
  );

  useEffect(() => {
    if (focusRequest !== null && focused !== null) {
      rows.current.get(focused)?.focus();
    }
  }, [focusRequest, focused]);

  const registerRow = useCallback((id: string, element: HTMLElement | null) => {
    if (element === null) rows.current.delete(id);
    else rows.current.set(id, element);
  }, []);
  const focusResults = useCallback(() => setFocusRequest({}), []);
  const [filterRequest, setFilterRequest] = useState<object | null>(null);
  const focusFilters = useCallback(() => setFilterRequest({}), []);
  const leaveSearch = useCallback(() => {
    setQuery('');
    setFocusRequest({});
  }, []);

  const open = useCallback((id: string) => {
    setFocusedId(id);
    setOpenId(id);
  }, []);
  const close = useCallback(() => setOpenId(null), []);

  const toggleDocType = useCallback((docType: ArchiveDocType) => {
    setDocTypes((current) => {
      const next = new Set(current);
      if (next.has(docType)) next.delete(docType);
      else next.add(docType);
      return next;
    });
  }, []);

  const clearFilters = useCallback(() => {
    setDocTypes(new Set());
    setFromText('');
    setToText('');
    setIncludeUndated(false);
    setPartner(null);
    setIncludeUnfiled(false);
  }, []);

  const loadMore = useCallback(() => {
    const last = items.at(-1);
    // ⚠️ CSAK KÉSZ ÁLLAPOTBAN. Szűrésváltás közben a `page` még a RÉGI lista;
    // az ő utolsó sorát kurzornak véve a régi és az új találatok keverednének.
    if (status === 'ready' && params !== null && page?.hasMore && last) {
      fetchPage(params, last.id);
    }
  }, [status, params, page, items, fetchPage]);

  const shortcuts = useMemo<Shortcut[]>(
    () => [
      { key: '/', labelKey: 'shortcut.search', run: () => setSearchRequest({}) },
      // A `←` A KERESŐBE VISZ (Viktor, 10-26) — a lista bal szélén a kereső
      // áll, a nyíl oda mutat. Mezőben gépelve a `←` a kurzoré marad
      // (a regiszter beviteli mezőben nem fut), a papíron pedig a néző
      // saját bal-széle (`onExit`) előbb kapja meg.
      { key: 'ArrowLeft', labelKey: 'shortcut.search', run: () => setSearchRequest({}) },
      // Az időszak mezője csak a LISTA nézeten áll — megnyitva az `i` semmit
      // nem tenne, és a súgó se ígérje.
      ...(openId === null
        ? [{ key: 'i', labelKey: 'shortcut.archivePeriod', run: () => setPeriodRequest({}) }]
        : []),
      { key: 'ArrowDown', labelKey: 'shortcut.nextRow', run: () => move(1) },
      { key: 'ArrowUp', labelKey: 'shortcut.prevRow', run: () => move(-1) },
      { key: 'j', labelKey: 'shortcut.nextRow', run: () => move(1) },
      { key: 'k', labelKey: 'shortcut.prevRow', run: () => move(-1) },
      {
        key: 'Enter',
        labelKey: 'shortcut.archiveOpen',
        run: () => (focused === null ? undefined : open(focused)),
      },
      {
        key: 'ArrowRight',
        labelKey: 'shortcut.archiveOpen',
        run: () => (focused === null ? undefined : open(focused)),
      },
      // AZ `Esc` CSAK MEGNYITOTT BIZONYLATON a Tár-é. Listán a váz `Esc`-e él
      // (súgó, teljes képernyő) — a regiszter az első találatot futtatja,
      // tehát egy itt hagyott üres `Esc` azt némítaná el.
      ...(openId === null
        ? []
        : [
            {
              key: 'Escape',
              labelKey: 'shortcut.archiveBack',
              allowInInput: true,
              // Teljes képernyőn előbb onnan lép ki: egy leütés, egy réteg.
              run: () => {
                if (document.fullscreenElement) {
                  void document.exitFullscreen?.().catch(() => undefined);
                  return;
                }
                close();
              },
            },
          ]),
    ],
    [move, focused, open, close, openId]
  );

  return {
    status,
    items,
    total: page?.total ?? 0,
    hasMore: page?.hasMore ?? false,
    facets: page?.facets ?? null,
    query,
    setQuery,
    docTypes,
    toggleDocType,
    fromText,
    toText,
    setFromText,
    setToText,
    from,
    to,
    includeUndated,
    setIncludeUndated,
    includeUnfiled,
    setIncludeUnfiled,
    unfiled: page?.unfiled ?? 0,
    partner,
    setPartner,
    clearFilters,
    loadMore,
    focusedId: focused,
    focus: setFocusedId,
    openId,
    open,
    close,
    searchRequest,
    periodRequest,
    registerRow,
    focusResults,
    leaveSearch,
    filterRequest,
    focusFilters,
    shortcuts,
  };
}
